import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Lock, Mail, CheckCircle, Key } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EngineerLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login'); // 'login', 'forgot', 'reset'
  const [resetEmailSent, setResetEmailSent] = useState(false);
  
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  const [forgotEmail, setForgotEmail] = useState('');
  const [resetForm, setResetForm] = useState({
    token: searchParams.get('reset') || '',
    newPassword: '',
    confirmPassword: ''
  });

  // Check for reset token in URL on mount
  React.useEffect(() => {
    const resetToken = searchParams.get('reset');
    if (resetToken) {
      setResetForm(prev => ({ ...prev, token: resetToken }));
      setMode('reset');
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/engineer/login`, {
        email: loginForm.email.trim().toLowerCase(),
        password: loginForm.password
      });
      
      if (response.data.success) {
        localStorage.setItem('engineer_token', response.data.token);
        localStorage.setItem('engineer_data', JSON.stringify(response.data.engineer));
        
        toast({
          title: "Login Successful",
          description: `Welcome back, ${response.data.engineer.fullName}!`
        });
        navigate('/engineer/dashboard');
      } else {
        const errorMsg = response.data.error || "Login failed. Please check your details.";
        toast({
          title: "Login Failed",
          description: errorMsg,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || "Invalid email or password";
      toast({
        title: "Login Failed",
        description: errorMsg,
        variant: "destructive"
      });
      alert("Login Failed: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/engineer/request-password-reset`, {
        email: forgotEmail.trim().toLowerCase()
      });
      setResetEmailSent(true);
      toast({
        title: "Check Your Email",
        description: "If an account exists, you'll receive a reset link."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not process request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetForm.newPassword !== resetForm.confirmPassword) {
      toast({ title: "Error", description: "Passwords don't match", variant: "destructive" });
      alert("Passwords don't match");
      return;
    }
    if (resetForm.newPassword.length < 8) {
      toast({ title: "Error", description: "Password must be at least 8 characters", variant: "destructive" });
      alert("Password must be at least 8 characters");
      return;
    }
    if (!resetForm.token) {
      toast({ title: "Error", description: "Invalid reset link", variant: "destructive" });
      alert("Invalid reset link - no token found");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/engineer/reset-password`, {
        token: resetForm.token,
        newPassword: resetForm.newPassword
      });
      if (response.data.success) {
        toast({ title: "Success!", description: "Password reset. You can now log in." });
        alert("Password reset successfully! You can now log in.");
        setMode('login');
      } else {
        const msg = response.data.error || "Failed to reset password";
        toast({ title: "Error", description: msg, variant: "destructive" });
        alert("Error: " + msg);
      }
    } catch (error) {
      console.error("Reset error:", error);
      const msg = error.response?.data?.detail || error.response?.data?.error || "Reset link expired. Please request a new one.";
      toast({ title: "Error", description: msg, variant: "destructive" });
      alert("Error: " + msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="engineer-login-page">
      <div className="container">
        <Card className="login-card">
          <CardHeader>
            <CardTitle className="text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_trustygas/artifacts/191wqlf3_file_000000004ca871f48b719ce9d2a32667.png" 
                alt="The Gas Guide"
                className="login-logo"
              />
              <h2>Engineer Portal</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="login-form">
                <div className="login-info">
                  <p>Log in with your email address and password.</p>
                  <p className="login-hint">First time? Use your <strong>Gas Safe Number</strong> as your password.</p>
                </div>
                
                <div className="form-field">
                  <label><Mail size={16} /> Email Address</label>
                  <Input
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    placeholder="Your registered email"
                    data-testid="login-email"
                  />
                </div>
                
                <div className="form-field">
                  <label><Lock size={16} /> Password</label>
                  <Input
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    placeholder="Your password or Gas Safe number"
                    data-testid="login-password"
                  />
                </div>
                
                <Button type="submit" className="btn-primary btn-full" disabled={loading} data-testid="login-submit">
                  {loading ? 'Logging in...' : 'Login'}
                </Button>

                <div className="login-links">
                  <button type="button" className="link-btn" onClick={() => setMode('forgot')}>
                    Forgot password?
                  </button>
                </div>
              </form>
            )}

            {mode === 'forgot' && !resetEmailSent && (
              <form onSubmit={handleForgotPassword} className="login-form">
                <div className="login-info">
                  <Key size={24} />
                  <p>Enter your email and we'll send you a reset link.</p>
                </div>
                
                <div className="form-field">
                  <label><Mail size={16} /> Email Address</label>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    placeholder="Your registered email"
                  />
                </div>
                
                <Button type="submit" className="btn-primary btn-full" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <div className="login-links">
                  <button type="button" className="link-btn" onClick={() => setMode('login')}>
                    ← Back to login
                  </button>
                </div>
              </form>
            )}

            {mode === 'forgot' && resetEmailSent && (
              <div className="reset-email-sent">
                <CheckCircle size={48} className="success-icon" />
                <h3>Check Your Email</h3>
                <p>If an account exists, you'll receive a password reset link.</p>
                <p className="email-hint">Don't see it? Check your spam folder.</p>
                <Button variant="outline" onClick={() => { setResetEmailSent(false); setMode('login'); }} className="btn-full">
                  Back to Login
                </Button>
              </div>
            )}

            {mode === 'reset' && (
              <form onSubmit={handleResetPassword} className="login-form">
                <div className="login-info">
                  <Key size={24} />
                  <p>Create your new password below.</p>
                </div>
                
                <div className="form-field">
                  <label><Lock size={16} /> New Password</label>
                  <Input
                    type="password"
                    value={resetForm.newPassword}
                    onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
                    required
                    placeholder="Minimum 8 characters"
                  />
                </div>
                
                <div className="form-field">
                  <label><Lock size={16} /> Confirm Password</label>
                  <Input
                    type="password"
                    value={resetForm.confirmPassword}
                    onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                    required
                    placeholder="Confirm new password"
                  />
                </div>
                
                <Button type="submit" className="btn-primary btn-full" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}

            {mode === 'login' && (
              <p className="login-help">
                Not yet approved? Your application is being reviewed.<br/>
                <span className="help-contact">Problems? Email admin@thegasguide.co.uk</span>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EngineerLogin;
